import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import { IconButton, Tooltip } from '@mui/material';
import { useThemeMode } from '../../context/ThemeContext';
import { loginUser } from '../../services/api';

function Login() {
  const navigate = useNavigate();
  const { cycleTheme, mode } = useThemeMode();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Stable device fingerprint (browsers cannot expose real MAC address)
      let deviceFingerprint = localStorage.getItem('erp_device_fingerprint');
      if (!deviceFingerprint) {
        deviceFingerprint = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('erp_device_fingerprint', deviceFingerprint);
      }

      const response = await loginUser({
        ...formData,
        login_url: window.location.href,
        device_fingerprint: deviceFingerprint
      });
      
      console.log('Login response:', response);
      
      if (response.success && response.data) {
        // Save token and user data from real API
        const user = response.data.user || {};
        const displayName =
          user.full_name || user.name || user.username || user.email || 'User';
        localStorage.setItem('erp_auth_token', response.data.token);
        if (response.data.login_session_id) {
          localStorage.setItem('erp_login_session_id', response.data.login_session_id);
        }
        localStorage.setItem('erp_user_role', user.role || 'staff');
        localStorage.setItem('erp_username', displayName);
        localStorage.setItem('erp_user_email', user.email || '');
        localStorage.setItem('erp_user_id', user.id);
        const branchName = user.branch_name || user.branch?.name || '';
        const branchCode = user.branch_code || user.branch?.branch_code || '';
        localStorage.setItem('erp_user', JSON.stringify({
          id: user.id,
          full_name: user.full_name || user.name || displayName,
          name: user.name || user.full_name || displayName,
          username: user.username || '',
          email: user.email || '',
          role: user.role || 'staff',
          branch_id: user.branch_id || null,
          branch_name: branchName || null,
          branch_code: branchCode || null
        }));
        if (user.branch_id) {
          localStorage.setItem('erp_branch_id', user.branch_id);
        } else {
          localStorage.removeItem('erp_branch_id');
        }
        if (branchName) localStorage.setItem('erp_branch_name', branchName);
        else localStorage.removeItem('erp_branch_name');
        if (branchCode) localStorage.setItem('erp_branch_code', branchCode);
        else localStorage.removeItem('erp_branch_code');
        
        console.log('Login successful, token saved');
        
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        setError(response.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error details:', err);
      setError(err.message || 'Cannot connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
        position: 'relative'
      }}>
        <Tooltip title={`Theme: ${mode} (click to change)`}>
          <IconButton onClick={cycleTheme} sx={{ position: 'absolute', top: 16, right: 16 }}>
            {mode === 'dark' ? <DarkModeIcon /> : mode === 'white' ? <LightModeIcon /> : <WbSunnyOutlinedIcon />}
          </IconButton>
        </Tooltip>
        <Paper sx={{ 
          p: 4, 
          width: '100%',
          boxShadow: 3
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            mb: 3 
          }}>
            <LockOutlinedIcon sx={{ 
              fontSize: 50, 
              color: 'primary.main', 
              mb: 2,
              bgcolor: 'primary.light',
              p: 1,
              borderRadius: '50%'
            }} />
            <Typography variant="h4" gutterBottom>
              ERP System
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Sign in to your account
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: 1
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              autoFocus
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ 
                mt: 1, 
                mb: 2,
                py: 1.5
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </form>
        </Paper>
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            © {new Date().getFullYear()} ERP System
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;